import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

export interface FeatureAnnouncement {
  id: string;
  title: string;
  description: string;
  linkText?: string;
  linkUrl?: string;
  badge?: string;
  badgeColor?: 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  version?: string; // バージョン表記（例: "v1.0.0", "2025.1"）
}

interface FeatureAnnouncementDialogProps {
  announcement: FeatureAnnouncement;
  storageKey?: string; // LocalStorageに保存するキー（指定すると「次回から表示しない」機能が有効になる）
  onClose?: () => void;
  onNavigate?: () => void; // カスタムナビゲーション処理（指定された場合はlinkUrlよりも優先）
}

const FeatureAnnouncementDialog: React.FC<FeatureAnnouncementDialogProps> = ({
  announcement,
  storageKey,
  onClose,
  onNavigate
}) => {
  const [open, setOpen] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  useEffect(() => {
    // LocalStorageにチェック済みかどうかを確認
    if (storageKey) {
      const dismissedId = localStorage.getItem(storageKey);
      // 保存されたIDと現在のIDが一致しない場合、または保存されていない場合に表示
      if (dismissedId !== announcement.id) {
        setOpen(true);
      }
    } else {
      setOpen(true);
    }
  }, [storageKey, announcement.id]);

  const handleClose = () => {
    if (dontShowAgain && storageKey) {
      // IDを保存（true/falseではなくIDを保存）
      localStorage.setItem(storageKey, announcement.id);
    }
    setOpen(false);
    onClose?.();
  };

  const handleNavigate = () => {
    if (onNavigate) {
      onNavigate();
    } else if (announcement.linkUrl) {
      window.location.href = announcement.linkUrl;
    }
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
          backdropFilter: 'blur(10px)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1, position: 'relative' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 36,
              height: 36,
              borderRadius: '8px',
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
            }}
          >
            <AutoAwesomeIcon sx={{ color: '#667eea', fontSize: '1.2rem' }} />
          </Box>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {announcement.title}
              </Typography>
              {announcement.badge && (
                <Chip
                  label={announcement.badge}
                  size="small"
                  color={announcement.badgeColor || 'primary'}
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 600
                  }}
                />
              )}
              {announcement.version && (
                <Chip
                  label={announcement.version}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'text.secondary'
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: 'text.secondary'
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, pb: 2 }}>
        <Typography
          variant="body1"
          sx={{
            color: 'text.primary',
            lineHeight: 1.3,
            whiteSpace: 'pre-wrap'
          }}
        >
          {announcement.description}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1.5 }}>
        {storageKey && (
          <FormControlLabel
            control={
              <Checkbox
                checked={dontShowAgain}
                onChange={(e) => setDontShowAgain(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                再度表示しない
              </Typography>
            }
            sx={{ mx: 0 }}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                color: 'text.secondary',
                borderColor: 'divider',
                '&:hover': {
                  borderColor: 'text.secondary'
                }
              }}
            >
              閉じる
            </Button>
            {(announcement.linkText && (announcement.linkUrl || onNavigate)) && (
              <Button
                onClick={handleNavigate}
                variant="contained"
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #63408a 100%)'
                  }
                }}
              >
                {announcement.linkText}
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              onClick={() => {
                window.location.href = '/release-notes';
                handleClose();
              }}
              size="small"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  color: 'primary.main'
                }
              }}
            >
              過去のリリースを確認する
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default FeatureAnnouncementDialog;
